// R-131-FIN-DECISION-F — map Pathway fields to FinancialAssumptions for detailed edit.

import { KW_PER_MW } from '../projectFinance';
import type { FinancialAssumptions, FinancialTechnology } from '../../types/financialAssumptions';
import { emptyFinancialAssumptions } from '../../types/financialAssumptions';
import { derivePathwayTimeline } from './pathwayCashflows';
import { applyCapacityOverride, applyCodYearOverride, pathwayFirstMwhDate } from './pathwayQuickEdit';
import type { Pathway, TechnologyId } from './types';

function mapTechnology(technology: TechnologyId): FinancialTechnology {
  switch (technology) {
    case 'nuclear-smr':
    case 'nuclear-large':
      return 'smr';
    case 'grid-tariff':
      return 'tbd';
    default:
      return technology as FinancialTechnology;
  }
}

function impliedCapacityFactorPct(pathway: Pathway): number | null {
  const operation = pathway.phases.find((p) => p.kind === 'operation');
  if (!operation?.annualMwh || pathway.capacityMW <= 0) {
    return pathway.revenue.availabilityPct;
  }
  const nameplateMwh = pathway.capacityMW * 8760;
  if (nameplateMwh <= 0) return null;
  return Math.round((operation.annualMwh / nameplateMwh) * 1000) / 10;
}

/** Seed a FinancialAssumptions object from a pathway for scoped detailed edit. */
export function financialAssumptionsFromPathway(
  pathway: Pathway,
  updatedBy: string,
  base?: FinancialAssumptions | null,
): FinancialAssumptions {
  const nowIso = new Date().toISOString();
  // C12 — SEED FROM EMPTY, NEVER FROM THE SITE'S OWN MODEL.
  //
  // `base` was the SITE's financial assumptions, spread in wholesale. Two
  // things came with it that do not belong to this pathway:
  //
  //  1. `phases[]`. Under `modelVersion: 3` the phase array is AUTHORITATIVE,
  //     so the record this function returns showed the site's phases while
  //     claiming to describe the pathway — and every campus value set below
  //     would be overridden by them on the next rollup. The dialog displayed
  //     one project and would have computed another.
  //  2. Every unmapped field (tax rate, ITC/PTC, debt terms, escalators…),
  //     silently attributing the site's financing to a benchmark technology
  //     option nobody had entered those terms for.
  //
  // `base` is still accepted and used for the two documented FALLBACKS below
  // (`constructionMonths`, `workingCapitalUsd`) where the pathway genuinely has
  // nothing to say — but it no longer forms the record.
  const seed = emptyFinancialAssumptions(nowIso, updatedBy);
  const fallback = base ?? seed;
  const timeline = derivePathwayTimeline(pathway, new Date());
  const codDate = pathwayFirstMwhDate(pathway) ?? `${timeline.codYear}-01-01`;
  const capexPerKw =
    pathway.capacityMW > 0
      ? pathway.capex.baseUsd / (pathway.capacityMW * KW_PER_MW)
      : null;
  const constructionMonths = pathway.phases
    .filter((p) => p.kind === 'construction')
    .reduce((sum, p) => sum + p.durationMonths, 0);

  return {
    ...seed,
    updatedAt: nowIso,
    updatedBy,
    technology: mapTechnology(pathway.technology),
    capacityMW: pathway.capacityMW,
    capacityFactorPct: impliedCapacityFactorPct(pathway),
    projectLifeYears: pathway.lifetime.designLifeYears,
    inServiceDate: codDate,
    capexTotalUsd: pathway.capex.baseUsd,
    capexPerKwUsd: capexPerKw,
    constructionMonths: constructionMonths > 0 ? constructionMonths : fallback.constructionMonths,
    decommissioningUsd: pathway.lifetime.decommissioningUsd,
    workingCapitalUsd: pathway.capex.workingCapitalUsd ?? fallback.workingCapitalUsd,
    contingencyPct: pathway.capex.contingencyPct,
    fixedOmPerKwYearUsd: pathway.opex.fixedOmPerKwYearUsd,
    variableOmPerMwhUsd: pathway.opex.variableOmPerMwhUsd,
    fuelCostPerMwhUsd: pathway.opex.fuelCostPerMwhUsd > 0 ? pathway.opex.fuelCostPerMwhUsd : null,
    waccPct: pathway.risk.baseWaccPct,
    ppaPricePerMwhUsd: pathway.revenue.ppaPricePerMwhUsd,
    ppaEscalatorPctPerYear: pathway.revenue.ppaEscalatorPctPerYear,
    ppaTermYears: pathway.revenue.ppaTermYears,
    // ONE PHASE, DESCRIBING THIS PATHWAY. `phases[]` is authoritative, so it
    // has to agree with the campus values above rather than carry a different
    // project's staging. A pathway is a single build, so a single phase is the
    // faithful representation.
    phases: [
      {
        phaseId: `pathway-${pathway.id}`,
        label: pathway.name,
        sequence: 1,
        capacityMW: pathway.capacityMW,
        capexTotalUsd: pathway.capex.baseUsd,
        capexPerKwUsd: capexPerKw,
        capacityFactorPct: impliedCapacityFactorPct(pathway),
        inServiceDate: codDate,
        constructionMonths: constructionMonths > 0 ? constructionMonths : null,
        fixedOmPerKwYearUsd: pathway.opex.fixedOmPerKwYearUsd,
        variableOmPerMwhUsd: pathway.opex.variableOmPerMwhUsd,
        fuelCostPerMwhUsd: pathway.opex.fuelCostPerMwhUsd > 0 ? pathway.opex.fuelCostPerMwhUsd : null,
        technology: mapTechnology(pathway.technology),
      },
    ],
  };
}

/** Finite-number guard for the reverse mapping. */
function num(v: number | null | undefined): number | null {
  return typeof v === 'number' && Number.isFinite(v) ? v : null;
}

/**
 * Apply an edited `FinancialAssumptions` back onto its pathway — the inverse of
 * `financialAssumptionsFromPathway`.
 *
 * C12 — the "Detailed assumptions" dialog let a user edit every field the
 * forward bridge sets, then mapped only ELEVEN of them back. A capacity factor,
 * an in-service date, a construction duration, a decommissioning cost or a
 * working-capital figure typed into that form was accepted, saved without
 * complaint, and silently discarded. Worse, `capacityMW` was written straight
 * onto the pathway, so re-sizing left every absolute-USD component at its old
 * size (the same defect as C10) — here it now goes through
 * `applyCapacityOverride`, which scales them.
 *
 * ORDER MATTERS and is deliberate:
 *   1. capacity (rescales absolutes and energy proportionally),
 *   2. COD (shifts the schedule and the decommissioning year),
 *   3. explicit per-field values, which override anything step 1 or 2 derived.
 * So a user who edits BOTH capacity and CAPEX gets the CAPEX they typed, not
 * the scaled one.
 *
 * NOT MAPPED, on purpose: `technology` (changing it means seeding a different
 * preset, not editing this one) and `capexPerKwUsd` (derived — `capexTotalUsd`
 * is the authority). Callers that expose those fields must hide them in this
 * scope rather than accept an edit they cannot honour.
 */
export function pathwayFromFinancialAssumptions(
  pathway: Pathway,
  fa: FinancialAssumptions,
  now: Date = new Date(),
): Pathway {
  let next = pathway;

  const capacityMW = num(fa.capacityMW);
  if (capacityMW != null && capacityMW > 0 && capacityMW !== pathway.capacityMW) {
    next = applyCapacityOverride(next, capacityMW);
  }

  const codYear = fa.inServiceDate ? Number.parseInt(fa.inServiceDate.slice(0, 4), 10) : NaN;
  if (Number.isFinite(codYear)) {
    const currentCod = derivePathwayTimeline(next, now).codYear;
    if (codYear !== currentCod) next = applyCodYearOverride(next, codYear, now);
  }

  const constructionMonths = num(fa.constructionMonths);
  const capacityFactorPct = num(fa.capacityFactorPct);
  const phases = next.phases.map((phase) => {
    if (phase.kind === 'construction' && constructionMonths != null && constructionMonths > 0) {
      return { ...phase, durationMonths: Math.max(1, Math.round(constructionMonths)) };
    }
    if (phase.kind === 'operation' && capacityFactorPct != null && capacityFactorPct > 0) {
      // The engine reads delivered ENERGY, not a capacity factor, so an edited
      // CF has to be re-expressed as annual MWh against the (possibly just
      // rescaled) nameplate or it moves nothing.
      const nameplateMwh = next.capacityMW * 8760;
      return { ...phase, annualMwh: nameplateMwh * (capacityFactorPct / 100) };
    }
    return phase;
  });

  const capexTotalUsd = num(fa.capexTotalUsd);
  const decommissioningUsd = num(fa.decommissioningUsd);
  const workingCapitalUsd = num(fa.workingCapitalUsd);
  const contingencyPct = num(fa.contingencyPct);

  return {
    ...next,
    phases,
    capex: {
      ...next.capex,
      ...(capexTotalUsd != null ? { baseUsd: capexTotalUsd } : {}),
      ...(contingencyPct != null ? { contingencyPct } : {}),
      ...(workingCapitalUsd != null ? { workingCapitalUsd } : {}),
    },
    opex: {
      ...next.opex,
      fixedOmPerKwYearUsd: num(fa.fixedOmPerKwYearUsd) ?? next.opex.fixedOmPerKwYearUsd,
      variableOmPerMwhUsd: num(fa.variableOmPerMwhUsd) ?? next.opex.variableOmPerMwhUsd,
      fuelCostPerMwhUsd: num(fa.fuelCostPerMwhUsd) ?? next.opex.fuelCostPerMwhUsd,
    },
    revenue: {
      ...next.revenue,
      ppaPricePerMwhUsd: num(fa.ppaPricePerMwhUsd) ?? next.revenue.ppaPricePerMwhUsd,
      ppaEscalatorPctPerYear:
        num(fa.ppaEscalatorPctPerYear) ?? next.revenue.ppaEscalatorPctPerYear,
      ppaTermYears: num(fa.ppaTermYears) ?? next.revenue.ppaTermYears,
    },
    risk: {
      ...next.risk,
      baseWaccPct: num(fa.waccPct) ?? next.risk.baseWaccPct,
    },
    lifetime: {
      ...next.lifetime,
      designLifeYears: num(fa.projectLifeYears) ?? next.lifetime.designLifeYears,
      ...(decommissioningUsd != null ? { decommissioningUsd } : {}),
    },
  };
}
